/**
 * Marketplace API calls — eBay connect/status/export/publish.
 *
 * @module portal/modules/apiOperations/marketplace
 */

import type { State } from '../types';
import { apiUrl } from './common';

export interface EbayStatus {
  configured: boolean;
  connected: boolean;
  environment?: string;
  connectedAt?: string;
}

export interface EbayExportResult {
  sku: string;
  offerId: string;
  title: string;
  price: number;
  imageCount: number;
  categoryId?: string;
}

export interface EbayPublishResult {
  listingId: string;
  listingUrl: string;
}

export class MarketplaceApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  method: 'GET' | 'POST',
  body: unknown,
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  retry = false
): Promise<T> {
  let token: string | null;
  try {
    token = await ensureAccessToken(retry);
  } catch {
    throw new MarketplaceApiError('Unable to acquire access token.', 401);
  }
  if (!token) throw new MarketplaceApiError('Unable to acquire access token.', 401);

  const response = await fetch(apiUrl(path), {
    method,
    headers: {
      Authorization: 'Bearer ' + token,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && !retry) {
    (state as { extAccessToken?: string | null }).extAccessToken = null;
    return request<T>(path, method, body, state, ensureAccessToken, true);
  }

  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new MarketplaceApiError(
      typeof data.error === 'string' ? data.error : `Request failed (${response.status})`,
      response.status,
      typeof data.code === 'string' ? data.code : undefined
    );
  }
  return data as T;
}

export function fetchEbayStatus(
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>
): Promise<EbayStatus> {
  return request<EbayStatus>('marketplace/ebay/status', 'GET', undefined, state, ensureAccessToken);
}

export async function startEbayConnect(
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>
): Promise<string> {
  const data = await request<{ authorizeUrl?: string }>(
    'marketplace/ebay/connect',
    'POST',
    {},
    state,
    ensureAccessToken
  );
  if (!data.authorizeUrl) throw new MarketplaceApiError('No authorize URL returned', 500);
  return data.authorizeUrl;
}

export function exportLotToEbayRemote(
  lotId: string,
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>
): Promise<EbayExportResult> {
  return request<EbayExportResult>(
    'marketplace/ebay/export',
    'POST',
    { lotId },
    state,
    ensureAccessToken
  );
}

export function publishEbayListingRemote(
  lotId: string,
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>
): Promise<EbayPublishResult> {
  return request<EbayPublishResult>(
    'marketplace/ebay/publish',
    'POST',
    { lotId },
    state,
    ensureAccessToken
  );
}
